<template>
  <div class="evaluation-container">
    <CCard class="staff-ui-card bg-white">
      <CCardHeader class="staff-ui-header">
        <div class="d-flex justify-content-between align-items-center flex-wrap">
          <h4 class="card-title mb-0 text-white d-flex align-items-center py-2">
            <CIcon name="cil-star" size="xl" class="mr-2"/> {{ $t('nav.evaluation') }}
          </h4>
          <div class="filter-group d-flex align-items-center flex-wrap py-2">
            <!-- Dept Filter -->
            <div class="evaluation-filter" v-if="isAdmin">
               <select v-model="selectedDepartment" class="form-control form-control-sm border-0" style="min-width: 140px;">
                 <option value="all">{{ $t('dashboard.filterAllDepts') }}</option>
                 <option v-for="dept in uniqueDepartments" :key="dept" :value="dept">{{ localizedDepartment(dept) }}</option>
               </select>
            </div>
            <div class="evaluation-filter" v-else>
               <span class="badge badge-light text-dark px-2 py-1 font-weight-bold border" style="font-size: 0.85rem;">
                 <CIcon name="cil-institution" class="mr-1 text-primary" />
                 {{ localizedDepartment(supervisorDepartmentName) || $t('evaluation.yourDepartment') || 'ฝ่ายงานของท่าน' }}
               </span>
            </div>
            
            <!-- Report Button -->
            <CButton color="success" size="sm" class="btn-report-excel my-1 ml-2" @click="openPdfModal">
              <CIcon name="cil-cloud-download" class="mr-1" /> {{ $t('dashboard.downloadExcel') || 'รายงาน Excel' }}
            </CButton>
          </div>
        </div>
      </CCardHeader>
      
      <CCardBody class="staff-ui-body">
        <p v-if="isLoading" role="status" class="text-muted">{{ $t('asyncFeedback.loading') }}</p>
        <div v-else-if="loadError" role="alert" class="alert alert-danger">
          {{ $t('mgmt.loadFail') }}
          <button type="button" class="btn btn-outline-primary" @click="fetchData">{{ $t('asyncFeedback.retry') }}</button>
        </div>
        <CRow v-show="hasLoaded && ((!isLoading && !loadError) || tickets.length || allTechnicians.length)">
          <CCol md="12" class="mb-4">
            <!-- Mobile View: Native Vue HTML Cards (matching BuildingList.vue / usersList.vue) -->
            <div class="evaluation-mobile-list d-md-none">
              <div class="staff-ui-mobile-search mb-3">
                <CIcon name="cil-search" />
                <input v-model.trim="mobileSearch" type="search" :placeholder="$t('evaluation.searchPlaceholder')" @input="mobileActivePage = 1" />
              </div>

              <article v-for="item in mobilePageEvaluation" :key="item._id" class="management-mobile-card mb-3 p-3 bg-white rounded-xl shadow-sm border">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <strong class="text-dark font-weight-bold" style="font-size: 1.05rem;">
                    <CIcon name="cil-user" class="mr-1 text-primary"/> {{ item.name }}
                  </strong>
                  <CBadge color="info">{{ item.department }}</CBadge>
                </div>

                <div class="p-3 rounded-lg mb-3" style="background-color: #f8fafc !important; border: 1px solid rgba(37, 99, 235, 0.12) !important;">
                  <div class="d-flex justify-content-between text-muted small mb-2">
                    <span>{{ $t('evaluation.field.totalJobs') }}: <strong class="text-dark">{{ item.totalJobs }}</strong></span>
                    <span>{{ $t('evaluation.field.completedJobs') }}: <strong class="text-success font-weight-bold">{{ item.completedJobs }}</strong></span>
                  </div>
                  
                  <div class="mb-2">
                    <div class="d-flex justify-content-between small mb-1">
                      <span>{{ $t('evaluation.field.slaMetPercent') }}: <strong class="text-dark">{{ item.slaMetPercent }}%</strong></span>
                      <small class="text-muted">{{ item.onTimeJobs }} / {{ item.completedJobs }}</small>
                    </div>
                    <CProgress class="progress-xs" :color="getSlaColor(item.slaMetPercent)" :value="item.slaMetPercent" />
                  </div>

                  <div class="d-flex justify-content-between align-items-center small">
                    <span>{{ $t('evaluation.field.avgRating') }}:</span>
                    <span :class="getRatingClass(item.avgRating)">
                      <CIcon name="cil-star" class="mr-1" v-if="item.avgRating > 0"/>
                      {{ item.avgRating > 0 ? item.avgRating.toFixed(1) : '-' }}
                    </span>
                  </div>
                </div>

                <CButton size="sm" class="btn-eval-details w-100" @click="viewDetails(item)">
                  <CIcon name="cil-magnifying-glass" class="mr-1"/> {{ $t('common.viewDetails') }}
                </CButton>
              </article>

              <div v-if="!mobilePageEvaluation.length" class="staff-ui-mobile-empty text-center p-4 text-muted bg-white rounded border">
                {{ $t('staffMgmt.noResults') || 'ไม่พบข้อมูล' }}
              </div>

              <div v-if="mobileTotalPages > 1" class="staff-ui-mobile-pagination d-flex justify-content-center align-items-center gap-2 mt-3">
                <button class="btn btn-sm btn-outline-secondary px-3" :disabled="mobileActivePage <= 1" @click="mobileActivePage--">‹</button>
                <span class="mx-2 font-weight-bold">{{ mobileActivePage }} / {{ mobileTotalPages }}</span>
                <button class="btn btn-sm btn-outline-secondary px-3" :disabled="mobileActivePage >= mobileTotalPages" @click="mobileActivePage++">›</button>
              </div>
            </div>

            <!-- Desktop View: CDataTable (d-none d-md-block) -->
            <div class="table-responsive-wrapper d-none d-md-block">
              <CDataTable class="custom-pagination evaluation-table"
                :key="tableKey"
                :items="evaluationData"
                :fields="evaluationFields"
                responsive
                :loading="isLoading"
                column-filter
                :items-per-page-select="{ values: [5, 10, 20, 50, 100] }"
                :items-per-page="10"
                hover
                sorter
                :pagination="{ doubleArrows: false, align: 'center' }"
                :table-filter="tableFilterOptions"
                cleaner
              >
                <!-- Technician Name Column -->
                <template #name="{item}">
                  <td class="font-weight-bold align-middle">
                    <span>{{ item.name }}</span>
                  </td>
                </template>
                
                <!-- Department Column -->
                <template #department="{item}">
                  <td class="align-middle">
                    <CBadge color="info">{{ item.department }}</CBadge>
                  </td>
                </template>

                <!-- Total Jobs Column -->
                <template #totalJobs="{item}">
                  <td class="text-center align-middle font-weight-bold">
                    <span>{{ item.totalJobs }}</span>
                  </td>
                </template>

                <!-- Completed Jobs Column -->
                <template #completedJobs="{item}">
                  <td class="text-center align-middle">
                    <span class="text-success font-weight-bold">{{ item.completedJobs }}</span>
                  </td>
                </template>

                <!-- SLA Met Column -->
                <template #slaMetPercent="{item}">
                  <td class="align-middle">
                    <div style="flex: 1; max-width: 180px;">
                      <div class="clearfix mb-1">
                        <div class="float-left">
                          <strong>{{ item.slaMetPercent }}%</strong>
                        </div>
                        <div class="float-right">
                          <small class="text-muted">{{ item.onTimeJobs }} / {{ item.completedJobs }}</small>
                        </div>
                      </div>
                      <CProgress 
                        class="progress-xs" 
                        :color="getSlaColor(item.slaMetPercent)" 
                        :value="item.slaMetPercent"
                      />
                    </div>
                  </td>
                </template>

                <!-- Average Rating Column -->
                <template #avgRating="{item}">
                  <td class="text-center align-middle">
                    <span :class="getRatingClass(item.avgRating)">
                      <CIcon name="cil-star" class="mr-1" style="margin-bottom: 2px;" v-if="item.avgRating > 0"/>
                      {{ item.avgRating > 0 ? item.avgRating.toFixed(1) : '-' }}
                    </span>
                  </td>
                </template>
                
                <!-- Action Column -->
                <template #action="{item}">
                  <td class="text-center align-middle">
                    <CButton size="sm" class="btn-eval-details w-100" @click="viewDetails(item)">
                      <CIcon name="cil-magnifying-glass" class="mr-1"/> {{ $t('common.viewDetails') }}
                    </CButton>
                  </td>
                </template>
              </CDataTable>
            </div>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>
    
    <!-- Details Modal -->
    <CModal
      size="xl"
      :show.sync="showDetailsModal"
      :close-on-backdrop="true"
      :close-button="false"
      centered
      class="evaluation-details-modal"
    >
      <template #header>
        <div class="w-100 d-flex align-items-center justify-content-between">
          <h5 class="modal-title mb-0 font-weight-bold text-white">
            <CIcon name="cil-list-rich" class="mr-2"/> {{ `${$t('evaluation.detailsFor')} ${selectedTechForDetails ? selectedTechForDetails.name : ''}` }}
          </h5>
          <button 
            type="button" 
            class="btn btn-sm font-weight-bold px-3 rounded-pill shadow-sm ml-auto text-white"
            :class="{ 'admin-close-btn': isAdmin }"
            style="background: rgba(255, 255, 255, 0.2) !important; color: #ffffff !important; border: 1px solid rgba(255, 255, 255, 0.4) !important;"
            @click="showDetailsModal = false"
          >
            <CIcon v-if="isAdmin" name="cil-x" class="mr-1" />
            {{ $t('common.close') }}
          </button>
        </div>
      </template>

      <div v-if="selectedTechForDetails" class="pt-2">
         <div class="d-flex justify-content-between align-items-center mb-3 px-1">
           <h6 class="mb-0 font-weight-bold text-dark">{{ $t('evaluation.detailsFor') || 'รายละเอียดรายการประเมิน' }}</h6>
           <small class="text-muted font-weight-bold" v-if="selectedTechForDetails.tickets">{{ selectedTechForDetails.tickets.length }} {{ $t('common.items') || 'รายการ' }}</small>
         </div>

         <!-- Mobile View: Clean Card Layout for Modal Tickets (< 768px) -->
         <div class="modal-mobile-tickets d-md-none">
           <div class="staff-ui-mobile-search mb-3" v-if="selectedTechForDetails.tickets && selectedTechForDetails.tickets.length > 5">
             <CIcon name="cil-search" />
             <input v-model.trim="modalMobileSearch" type="search" :placeholder="$t('dashboard.searchPlaceholder') || 'ค้นหาใบแจ้งซ่อม หรือประเภทงาน...'" @input="modalMobilePage = 1" />
           </div>

           <article v-for="ticket in modalMobilePageTickets" :key="ticket.ticketNo" class="management-mobile-card mb-3 p-3 bg-white rounded-xl shadow-sm border">
             <div class="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
               <strong class="text-dark font-weight-bold" style="font-size: 1.05rem;">
                 <CIcon name="cil-description" class="mr-1 text-primary"/> {{ ticket.ticketNo }}
               </strong>
               <small class="text-muted"><CIcon name="cil-clock" class="mr-1"/>{{ ticket.dateInformed }}</small>
             </div>

             <div class="mb-2">
               <small class="text-muted d-block mb-1">{{ $t('dashboard.field.category') }}:</small>
               <strong class="text-dark">{{ ticket.category }}</strong>
             </div>

             <div class="d-flex justify-content-between align-items-center p-2 rounded mb-2" style="background-color: #f8fafc !important; border: 1px solid rgba(37, 99, 235, 0.08) !important;">
               <div>
                 <small class="text-muted d-block mb-1">{{ $t('dashboard.field.status') }}:</small>
                 <CBadge :color="getBadge(ticket._status_raw)">{{ ticket.status }}</CBadge>
               </div>
               <div class="text-right">
                 <small class="text-muted d-block mb-1">{{ $t('dashboard.field.sla') }}:</small>
                 <CBadge :color="ticket._slaOverdue ? 'danger' : 'success'">{{ ticket.sla }}</CBadge>
               </div>
             </div>

             <div class="d-flex justify-content-between align-items-center pt-1">
               <div>
                 <small class="text-muted mr-2">{{ $t('evaluation.field.rating') }}:</small>
                 <span class="text-warning font-weight-bold" v-if="ticket._stars > 0">{{ ticket._stars }}/5</span>
                 <span class="text-muted" v-else>-</span>
               </div>
               <div class="text-truncate ml-2" style="max-width: 60%;">
                 <small class="text-muted">{{ $t('evaluation.field.comment') }}: {{ ticket._comment || '-' }}</small>
               </div>
             </div>
           </article>

           <div v-if="!modalMobilePageTickets.length" class="staff-ui-mobile-empty text-center p-4 text-muted bg-white rounded border">
             {{ $t('staffMgmt.noResults') || 'ไม่พบรายการตั๋วซ่อม' }}
           </div>

           <div v-if="modalMobileTotalPages > 1" class="staff-ui-mobile-pagination d-flex justify-content-center align-items-center gap-2 mt-3">
             <button class="btn btn-sm btn-outline-secondary px-3" :disabled="modalMobilePage <= 1" @click="modalMobilePage--">‹</button>
             <span class="mx-2 font-weight-bold">{{ modalMobilePage }} / {{ modalMobileTotalPages }}</span>
             <button class="btn btn-sm btn-outline-secondary px-3" :disabled="modalMobilePage >= modalMobileTotalPages" @click="modalMobilePage++">›</button>
           </div>
         </div>

         <!-- Desktop View: CDataTable (>= 768px) -->
         <div class="table-responsive-wrapper d-none d-md-block">
               <CDataTable
                 class="custom-pagination"
                 :items="selectedTechForDetails.tickets"
                 :fields="ticketFields"
                 column-filter
                 :items-per-page="10"
                 hover
                 sorter
                 :pagination="{ doubleArrows: false, align: 'center' }"
                 cleaner
               >
                 <template #ticketNo="{item}">
                   <td class="font-weight-bold">
                     <span>{{ item.ticketNo }}</span>
                   </td>
                 </template>
                 <template #status="{item}">
                   <td>
                     <CBadge :color="getBadge(item._status_raw)">
                       {{ item.status }}
                     </CBadge>
                   </td>
                 </template>
                 <template #sla="{item}">
                   <td>
                     <CBadge :color="item._slaOverdue ? 'danger' : 'success'">
                       {{ item.sla }}
                     </CBadge>
                   </td>
                 </template>
                 <template #rating="{item}">
                   <td class="text-center">
                     <span class="text-warning font-weight-bold" v-if="item._stars > 0">
                       {{ item._stars }}/5
                     </span>
                     <span class="text-muted" v-else>-</span>
                   </td>
                 </template>
                 <template #comment="{item}">
                   <td>
                     <span>{{ item._comment || '-' }}</span>
                   </td>
                 </template>
               </CDataTable>
         </div>
      </div>
      <template #footer>
        <span class="d-none"></span>
      </template>
    </CModal>

    <!-- Report Excel Modal -->
    <CModal 
      :show.sync="showPdfModal" 
      class="report-export-modal"
      :close-button="false"
      size="lg"
    >
      <template #header>
        <div class="d-flex align-items-center w-100">
          <div class="report-modal-icon-badge mr-2">
            <CIcon name="cil-cloud-download" size="lg" />
          </div>
          <div>
            <h5 class="modal-title mb-0 font-weight-bold text-white">
              {{ $t('dashboard.downloadExcel') || 'ดาวน์โหลดรายงาน Excel' }}
            </h5>
            <small class="text-white-50">
              {{ $t('evaluation.excelExportSubtitle') || 'ระบบจัดการและส่งออกข้อมูลรายงาน SmartFix MFU' }}
            </small>
          </div>
        </div>
      </template>

      <div class="report-export-modal-body p-2">
        <!-- Quick Preset Filter Buttons -->
        <div class="mb-3">
          <label class="font-weight-bold text-dark d-block mb-2">
            {{ $t('evaluation.quickPresets') || 'ช่วงเวลาด่วน:' }}
          </label>
          <div class="btn-group btn-group-sm flex-wrap w-100 preset-btn-group" role="group">
            <button 
              type="button" 
              class="btn btn-outline-primary"
              :class="{ active: !pdfStartDate && !pdfEndDate }"
              @click="setDatePreset('all')"
            >
              {{ $t('evaluation.presetAll') || 'ทั้งหมด' }}
            </button>
            <button 
              type="button" 
              class="btn btn-outline-primary"
              @click="setDatePreset('thisMonth')"
            >
              {{ $t('evaluation.presetThisMonth') || 'เดือนนี้' }}
            </button>
            <button 
              type="button" 
              class="btn btn-outline-primary"
              @click="setDatePreset('last30Days')"
            >
              {{ $t('evaluation.presetLast30Days') || '30 วันล่าสุด' }}
            </button>
            <button 
              type="button" 
              class="btn btn-outline-primary"
              @click="setDatePreset('thisYear')"
            >
              {{ $t('evaluation.presetThisYear') || 'ปีนี้' }}
            </button>
          </div>
        </div>

        <!-- Custom Date Range Form -->
        <div class="row">
          <div class="col-md-6 form-group">
            <label class="font-weight-bold text-dark">
              <CIcon name="cil-calendar" class="mr-1 text-primary" />
              {{ $t('dashboard.dateFrom') || 'ตั้งแต่วันที่' }}
            </label>
            <input type="date" class="form-control" v-model="pdfStartDate">
          </div>
          <div class="col-md-6 form-group">
            <label class="font-weight-bold text-dark">
              <CIcon name="cil-calendar" class="mr-1 text-primary" />
              {{ $t('dashboard.dateTo') || 'ถึงวันที่' }}
            </label>
            <input type="date" class="form-control" v-model="pdfEndDate">
          </div>
        </div>

        <!-- Department Filter for Report -->
        <div class="form-group mb-3">
          <label class="font-weight-bold text-dark mb-1">
            <CIcon name="cil-institution" class="mr-1 text-primary" />
            {{ $t('dashboard.field.department') || 'หน่วยงาน / แผนก' }}:
          </label>

          <!-- Admin: Can choose All or specific department -->
          <div v-if="isAdmin">
            <select v-model="excelDepartment" class="form-control">
              <option value="all">{{ $t('evaluation.allDepartments') || 'ทุกแผนก (ทั้งหมด)' }}</option>
              <option v-for="dept in uniqueDepartments" :key="`modal-dept-${dept}`" :value="dept">
                {{ localizedDepartment(dept) }}
              </option>
            </select>
          </div>

          <!-- Supervisor: Locked to supervisor's own department -->
          <div v-else>
            <div class="input-group">
              <input 
                type="text" 
                class="form-control bg-light font-weight-bold text-dark" 
                :value="localizedDepartment(supervisorDepartmentName) || $t('evaluation.yourDepartment') || $t('common.other')" 
                readonly 
                disabled
              />
              <div class="input-group-append">
                <span class="input-group-text bg-light text-muted">
                  <CIcon name="cil-lock-locked" />
                </span>
              </div>
            </div>
            <small class="text-muted mt-1 d-block">
              {{ $t('evaluation.supervisorDepartmentNote') || '* สิทธิ์หัวหน้างาน (Supervisor) สามารถดาวน์โหลดรายงานและผลการประเมินได้เฉพาะฝ่ายตัวเองเท่านั้น' }}
            </small>
          </div>
        </div>

        <!-- Report Details & Live Preview Box -->
        <div class="report-scope-preview-card p-3 rounded mb-2 border">
          <div class="d-flex justify-content-between align-items-center flex-wrap">
            <span class="text-muted">
              <strong>{{ $t('evaluation.scopeDepartment') || 'ขอบเขตแผนก:' }}</strong> 
              <span class="badge badge-info ml-1 px-2 py-1">
                {{ effectiveExcelDepartment === 'all' ? ($t('evaluation.allDepartments') || 'ทุกแผนก (ทั้งหมด)') : localizedDepartment(effectiveExcelDepartment) }}
              </span>
            </span>
            <span class="text-muted">
              <strong>{{ $t('evaluation.matchingTickets') || 'จำนวนใบงานที่เข้าเงื่อนไข:' }}</strong> 
              <span class="badge badge-success ml-1 px-2 py-1">
                {{ $t('evaluation.itemsCount', { count: previewReportCount }) || `${previewReportCount} รายการ` }}
              </span>
            </span>
          </div>
        </div>
      </div>

      <template #footer>
        <CButton color="secondary" :class="{ 'admin-action-btn': isAdmin }" @click="cancelPdfModal">
          {{ $t('sendReport.cancel') }}
        </CButton>
        <CButton 
          color="success" 
          class="px-4 font-weight-bold admin-action-btn" 
          @click="downloadExcelReport" 
          :disabled="isExportingReport"
          style="background-color: #10b981 !important; border-color: #10b981 !important; color: #ffffff !important; margin-left: 8px;"
        >
          {{ isExportingReport ? 'กำลังสร้างไฟล์...' : ($t('dashboard.downloadExcel') || 'ดาวน์โหลดไฟล์ Excel (.xlsx)') }}
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import CModal from '@/components/common/AccessibleModal.vue';
import Swal from 'sweetalert2';
import { assertListResponse, queueLatestListLoad, disposeListLoad } from "@/utils/asyncFeedback";
import axios from 'axios'
import moment from 'moment'
import { exportSmartFixExcelReport } from '@/utils/excelReportExport'
import {
  getTicketLocationFull,
  getTicketAvailableTime,
  getTicketReporterAffiliation,
  getTicketUserName,
  getTicketMaintenanceImage
} from '@/utils/ticketViewHelper'
import { translateDepartment, translateSection } from '@/utils/categoryi18n'

const getApiUrl = () => (process.env.VUE_APP_APP_URL || '').replace(/\/$/, '')

export default {
  components: { CModal },
  name: 'TechnicianEvaluation',
  data() {
    return {
      tickets: [],
      categories: [],
      allTechnicians: [],
      selectedDepartment: 'all',
      evalMonths: 6,
      showDetailsModal: false,
      selectedTechForDetails: null,
      isLoading: false,
      loadError: false,
      hasLoaded: false,
      tableKey: 0,
      mobileSearch: '',
      mobileActivePage: 1,
      modalMobileSearch: '',
      modalMobilePage: 1,
      showPdfModal: false,
      excelDepartment: 'all',
      pdfStartDate: '',
      pdfEndDate: '',
      isExportingReport: false
    }
  },
  computed: {
    modalMobileFilteredTickets() {
      if (!this.selectedTechForDetails?.tickets) return [];
      const kw = (this.modalMobileSearch || '').toLowerCase();
      if (!kw) return this.selectedTechForDetails.tickets;
      return this.selectedTechForDetails.tickets.filter(t =>
        (t.ticketNo || '').toLowerCase().includes(kw) ||
        (t.category || '').toLowerCase().includes(kw) ||
        (t.status || '').toLowerCase().includes(kw)
      );
    },
    modalMobilePageTickets() {
      const start = (this.modalMobilePage - 1) * 5;
      return this.modalMobileFilteredTickets.slice(start, start + 5);
    },
    modalMobileTotalPages() {
      return Math.max(1, Math.ceil(this.modalMobileFilteredTickets.length / 5));
    },
    currentLang() {
      return this.$store?.state?.locale?.lang || 'th'
    },
    isEn() {
      return this.currentLang === 'en'
    },
    isAdmin() {
      if (this.$route && this.$route.meta && Array.isArray(this.$route.meta.roles)) {
        if (this.$route.meta.roles.includes('admin')) return true;
        if (this.$route.meta.roles.includes('supervisor')) return false;
      }
      const role = this.$store?.state?.auth?.user?.role || this.$store?.getters?.['auth/profile']?.role;
      if (role === 'admin') return true;
      if (role === 'supervisor') return false;
      return Boolean(this.$route && this.$route.path && this.$route.path.startsWith('/admin/'));
    },
    supervisorSectionId() {
      const user = this.$store?.state?.auth?.profile || this.$store?.getters?.['auth/profile'] || this.$store?.state?.auth?.user;
      const mySec = user?.section || user?.department;
      return mySec && typeof mySec === 'object'
        ? String(mySec._id || mySec.id || '')
        : String(mySec || '');
    },
    supervisorDepartmentName() {
      const user = this.$store?.state?.auth?.profile || this.$store?.getters?.['auth/profile'] || this.$store?.state?.auth?.user;
      if (!user) return '';

      // 1. Direct section object
      const sec = user.section;
      if (sec && typeof sec === 'object') {
        const name = sec.departmentName || sec.name || sec.code;
        if (name) return name;
      }

      // 2. Direct department object
      const dept = user.department;
      if (dept && typeof dept === 'object') {
        const name = dept.departmentName || dept.name || dept.code;
        if (name) return name;
      }

      // 3. Match by ID against categories
      const secId = this.supervisorSectionId;
      if (secId && this.categories?.length) {
        const matched = this.categories.find(c => String(c._id || c.id) === secId);
        if (matched) return matched.departmentName || matched.name || '';
      }

      // 4. Section string name directly (if not raw 24-hex ObjectId)
      if (typeof sec === 'string' && sec.length > 0 && !/^[0-9a-fA-F]{24}$/.test(sec)) {
        return sec;
      }
      if (typeof dept === 'string' && dept.length > 0 && !/^[0-9a-fA-F]{24}$/.test(dept)) {
        return dept;
      }

      // 5. Fallback: match from technicians belonging to supervisor's section
      if (secId && this.allTechnicians?.length) {
        const tech = this.allTechnicians.find(t => {
          const tSec = t.section;
          const tSecId = String(tSec?._id || tSec?.id || tSec || '');
          return tSecId === secId;
        });
        if (tech) {
          const dName = tech.department?.name || tech.department?.departmentName || tech.section?.departmentName || (typeof tech.department === 'string' ? tech.department : '');
          if (typeof dName === 'string' && dName) return dName;
        }
      }

      return '';
    },
    effectiveExcelDepartment() {
      if (!this.isAdmin) {
        return this.supervisorDepartmentName || 'own';
      }
      return this.excelDepartment || 'all';
    },
    tableFilterOptions() {
      void this.$store.state.locale.lang
      return {
        label: this.$t('common.filterLabel') || 'ค้นหา:',
        placeholder: this.$t('evaluation.searchPlaceholder') || 'ค้นหาชื่อช่าง หรือ แผนก...'
      }
    },
    mobileFilteredEvaluation() {
      const kw = (this.mobileSearch || '').toLowerCase();
      if (!kw) return this.evaluationData;
      return this.evaluationData.filter(item =>
        (item.name || '').toLowerCase().includes(kw) ||
        (item.department || '').toLowerCase().includes(kw)
      );
    },
    previewReportCount() {
      const tickets = this.getFilteredTicketsForExport();
      return tickets.length;
    },
    mobilePageEvaluation() {
      const start = (this.mobileActivePage - 1) * 10;
      return this.mobileFilteredEvaluation.slice(start, start + 10);
    },
    mobileTotalPages() {
      return Math.max(1, Math.ceil(this.mobileFilteredEvaluation.length / 10));
    },
    uniqueDepartments() {
      if (!this.isAdmin) {
        const ownDept = this.supervisorDepartmentName;
        return ownDept ? [ownDept] : [];
      }
      const depts = new Set()
      this.categories.forEach(c => {
        if (c.departmentName) depts.add(c.departmentName)
        if (c.name) depts.add(c.name)
      })
      this.allTechnicians.forEach(t => {
        const dName = t.department?.name || t.department?.departmentName || t.section?.departmentName || t.department
        if (dName && typeof dName === 'string') depts.add(dName)
      })
      return Array.from(depts).sort()
    },
    evaluationFields() {
      void this.$store.state.locale.lang
      return [
        { key: 'name', label: this.$t('evaluation.field.name'), _style: 'min-width: 150px;' },
        { key: 'department', label: this.$t('evaluation.field.department') },
        { key: 'totalJobs', label: this.$t('evaluation.field.totalJobs'), _classes: 'text-center' },
        { key: 'completedJobs', label: this.$t('evaluation.field.completedJobs'), _classes: 'text-center' },
        { key: 'slaMetPercent', label: this.$t('evaluation.field.slaMetPercent'), _style: 'min-width: 120px;' },
        { key: 'avgRating', label: this.$t('evaluation.field.avgRating'), _classes: 'text-center' },
        { key: 'action', label: '', _style: 'width: 120px;', sorter: false, filter: false }
      ]
    },
    ticketFields() {
      void this.$store.state.locale.lang
      return [
        { key: 'ticketNo', label: this.$t('dashboard.field.ticketNo'), _classes: 'font-weight-bold', _style: 'width: 100px;' },
        { key: 'dateInformed', label: this.$t('dashboard.field.dateInformed') },
        { key: 'category', label: this.$t('dashboard.field.category') },
        { key: 'status', label: this.$t('dashboard.field.status') },
        { key: 'sla', label: this.$t('dashboard.field.sla') },
        { key: 'rating', label: this.$t('evaluation.field.rating'), _classes: 'text-center' },
        { key: 'comment', label: this.$t('evaluation.field.comment') }
      ]
    },
    evaluationData() {
      void this.currentLang
      // 1. Flatten categories for SLA lookup
      const flattenCategories = []
      this.categories.forEach(cat => {
        if(cat.subCategories && Array.isArray(cat.subCategories)) {
          cat.subCategories.forEach(sub => {
             flattenCategories.push({
               dep: cat.departmentName || cat.name,
               name: sub.name,
               sla: sub.slaHours
             })
          })
        }
      })

      // 2. Filter base tickets (exclude cancelled)
      const validTickets = (this.tickets || []).filter(t => {
          if (t.status === 'cancelled') return false
          const date = moment(t.userPart?.dateTimeInformed || t.createdAt)
          return date.isValid()
      })

      // 3. Filter technicians by department
      const mySecId = this.supervisorSectionId
      const baseTechs = (!this.isAdmin && mySecId)
        ? (this.allTechnicians || []).filter(tech => {
            const tSec = tech.section
            const tSecId = tSec && typeof tSec === 'object' ? String(tSec._id || tSec.id || '') : String(tSec || '')
            return !tSecId || tSecId === mySecId
          })
        : (this.allTechnicians || [])
      let displayTechs = baseTechs
      if (this.isAdmin && this.selectedDepartment !== 'all') {
          displayTechs = displayTechs.filter(tech => {
             const dName = tech.department?.name || tech.department?.departmentName || tech.section?.departmentName || tech.department
             const sName = tech.section?.departmentName || tech.section?.name || ''
             return dName === this.selectedDepartment || sName === this.selectedDepartment
          })
      }

      // 4. Calculate stats per technician
      return displayTechs.map(tech => {
          const techIdStr = String(tech._id || tech.id || '')
          const techTickets = validTickets.filter(t => this.isTicketAssignedTo(t, techIdStr))

          let totalJobs = techTickets.length
          let completedJobs = 0
          let onTimeJobs = 0
          let ratingSum = 0
          let ratedJobs = 0
          let formattedTicketsList = []

          techTickets.forEach(t => {
             // SLA Logic
             let slaHours = 24 
             const subName = t.userPart?.subCategory?.name || t.userPart?.subCategory
             const matched = flattenCategories.find(s => s.name === subName)
             if (matched) slaHours = matched.sla

             const startTime = moment(t.userPart?.dateTimeInformed || t.createdAt)
             const deadline = t.slaDeadline ? moment(t.slaDeadline) : startTime.clone().add(slaHours, 'hours')
             
             const isClosed = ['done', 'welldone'].includes(t.status)
             let endTime = moment()
             if (isClosed) {
                 endTime = moment(t.technicianPart?.dateTimeFinished || t.updatedAt)
                 completedJobs++
             }
             
             const isOverdue = endTime.isAfter(deadline)
             if (isClosed && !isOverdue) {
                 onTimeJobs++
             }

             // Rating Logic
             const stars = t.rating?.stars || 0
             if (stars > 0) {
                 ratingSum += stars
                 ratedJobs++
             }

             // Format Ticket for Details Modal
             formattedTicketsList.push({
                ticketNo: t.ticketNumber || (t.id || t._id || '').substring(0, 8).toUpperCase(),
                dateInformed: this.formatDate(t.userPart?.dateTimeInformed || t.createdAt),
                category: subName || '-',
                status: this.getStatusLabel(t.status),
                sla: isOverdue ? this.$t('dashboard.slaOverdue') : this.$t('dashboard.slaOnTime'),
                _status_raw: t.status,
                _slaOverdue: isOverdue,
                _stars: stars,
                _comment: t.rating?.comment || ''
             })
          })

          const slaMetPercent = completedJobs > 0 ? Math.round((onTimeJobs / completedJobs) * 100) : 0
          const avgRating = ratedJobs > 0 ? (ratingSum / ratedJobs) : 0

          return {
             _id: tech._id,
             name: `${tech.name || ''} ${tech.surname || ''}`.trim() || tech.email || this.$t('common.technician') || 'ช่างเทคนิค',
             department: this.localizedDepartment(tech.department?.name || tech.department?.departmentName || tech.section?.departmentName || (typeof tech.department === 'string' ? tech.department : this.$t('common.other'))),
             totalJobs,
             completedJobs,
             onTimeJobs,
             slaMetPercent,
             avgRating,
             tickets: formattedTicketsList.sort((a,b) => moment(b.dateInformed, 'DD/MM/YYYY HH:mm').diff(moment(a.dateInformed, 'DD/MM/YYYY HH:mm')))
          }
      })
    }
  },
  created() {
    this.fetchData()
  },
  mounted() { if (false)
    this.fetchData()
  },
  activated() {
    this.fetchData()
  },
  watch: {
    supervisorDepartmentName: {
      immediate: true,
      handler(newDept) {
        if (!this.isAdmin && newDept) {
          this.excelDepartment = newDept
        }
      }
    },
    '$store.state.locale.lang'() {
      this.tableKey++
      if (this.selectedTechForDetails) {
        const updatedTech = this.evaluationData.find(item => item._id === this.selectedTechForDetails._id)
        if (updatedTech) {
          this.selectedTechForDetails = updatedTech
        }
      }
    },
    '$route': {
      immediate: false,
      handler(to) {
        if (to && (to.name === 'ATechnicianEvaluation' || to.name === 'STechnicianEvaluation' || to.name === 'TechnicianEvaluation' || to.path?.includes('evaluation'))) {
          this.fetchData()
        }
      }
    }
  },
  beforeDestroy() {
    disposeListLoad(this);
  },
  methods: {
    getTicketAssignments(ticket) {
      const assignments = ticket?.supervisorPart?.assignments
      if (Array.isArray(assignments) && assignments.length) {
        return assignments.filter(item => item && item.status !== 'rejected').map(item => ({
          assignee: item.assignee,
          percentage: Number(item.percentage) || 0
        }))
      }

      const legacy = ticket?.supervisorPart?.assignedTechnician
      return legacy ? [{ assignee: legacy, percentage: 100 }] : []
    },
    getAssigneeId(assignment) {
      return String(assignment?.assignee?._id || assignment?.assignee || '')
    },
    isTicketAssignedTo(ticket, technicianId) {
      return this.getTicketAssignments(ticket).some(
        assignment => this.getAssigneeId(assignment) === String(technicianId)
      )
    },
    getTicketParts(ticket) {
      const technicianPart = ticket?.technicianPart || {}
      if (Array.isArray(technicianPart.parts) && technicianPart.parts.length) {
        return technicianPart.parts.map(part => ({
          name: part.name || '-',
          quantity: Math.max(1, Number(part.quantity) || 1),
          unitPrice: Math.max(0, Number(part.unitPrice) || 0)
        }))
      }
      if (technicianPart.noPartsUsed) return []
      if (technicianPart.spareParts && technicianPart.spareParts !== '-') {
        return [{
          name: technicianPart.spareParts,
          quantity: Math.max(1, Number(technicianPart.quantity) || 1),
          unitPrice: Math.max(0, Number(technicianPart.cost) || 0)
        }]
      }
      if (Number(technicianPart.cost) > 0) {
        return [{
          name: this.$t('evaluation.generalExpense') || 'ค่าใช้จ่ายทั่วไป',
          quantity: 1,
          unitPrice: Number(technicianPart.cost)
        }]
      }
      return []
    },
    getTicketTotal(ticket) {
      const parts = this.getTicketParts(ticket)
      if (parts.length > 0) {
        return parts.reduce(
          (total, part) => total + (part.quantity * part.unitPrice),
          0
        )
      }
      const techPart = ticket?.technicianPart || {}
      return Number(techPart.cost) || 0
    },
    fetchData() {
      return queueLatestListLoad(this, async isCurrent => {
        this.loadError = false
        try {
          const token = localStorage.getItem('accessToken')
          const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {}
          const baseUrl = getApiUrl()

          const [ticketsRes, categoriesRes, techniciansRes] = await Promise.all([
            axios.get(`${baseUrl}/ticket/admin/all`, config),
            axios.get(`${baseUrl}/category`, config),
            axios.get(`${baseUrl}/user/technicians?includeSupervisors=true`, config)
          ])

          if (!isCurrent()) return
          assertListResponse(ticketsRes)
          assertListResponse(categoriesRes)
          assertListResponse(techniciansRes)

          if (ticketsRes.data?.success) {
            this.tickets = ticketsRes.data.data || []
          } else if (Array.isArray(ticketsRes.data)) {
            this.tickets = ticketsRes.data
          }

          if (categoriesRes.data?.success) {
            this.categories = categoriesRes.data.data || []
          } else if (Array.isArray(categoriesRes.data)) {
            this.categories = categoriesRes.data
          }

          if (techniciansRes?.data?.success) {
            this.allTechnicians = techniciansRes.data.data || []
          } else if (Array.isArray(techniciansRes?.data)) {
            this.allTechnicians = techniciansRes.data
          }
        
          // Force CDataTable to re-render with fresh items on mobile/desktop
          this.tableKey++
          this.hasLoaded = true
        } catch (error) {
          if (!isCurrent()) return;
          console.error("Error fetching evaluation data:", error)
          this.loadError = true
        }
      });
    },
    getSlaColor(percent) {
      if (percent >= 90) return 'success'
      if (percent >= 70) return 'warning'
      return 'danger'
    },
    getRatingClass(rating) {
      if (rating >= 4.5) return 'text-success font-weight-bold'
      if (rating >= 3.0) return 'text-warning font-weight-bold'
      if (rating > 0) return 'text-danger font-weight-bold'
      return 'text-muted'
    },
    formatDate(date) {
      if (!date) return '-'
      return moment(date).format('DD/MM/YYYY HH:mm')
    },
    getBadge(status) {
      switch (status) {
        case 'pending': return 'warning'
        case 'assigned': return 'info'
        case 'in_progress': return 'primary'
        case 'done': return 'success'
        case 'welldone': return 'success'
        case 'cancelled': return 'danger'
        case 'rejected': return 'danger'
        default: return 'secondary'
      }
    },
    getStatusLabel(status) {
        const key = `dashboard.status.${status}`
        const translated = this.$t(key)
        return translated !== key ? translated : status
    },
    viewDetails(techItem) {
      this.selectedTechForDetails = techItem
      this.modalMobileSearch = ''
      this.modalMobilePage = 1
      this.showDetailsModal = true
    },
    localizedDepartment(name) {
      if (!name || name === 'all') return this.$t('evaluation.allDepartments') || (this.isEn ? 'All Departments' : 'ทุกแผนก (ทั้งหมด)')
      const lang = this.currentLang
      const translated = translateDepartment(name, lang) || translateSection(name, lang)
      return translated || name
    },
    openPdfModal() {
      if (this.isAdmin) {
        this.excelDepartment = this.selectedDepartment || 'all'
      } else {
        this.excelDepartment = this.supervisorDepartmentName || 'own'
      }
      this.showPdfModal = true
    },
    cancelPdfModal() {
      this.showPdfModal = false
    },
    isTicketInDepartment(ticket, targetDept) {
      if (!targetDept || targetDept === 'all') return true

      const normTarget = String(targetDept).trim().toLowerCase()
      const targetEn = (translateDepartment(targetDept, 'en') || translateSection(targetDept, 'en') || '').trim().toLowerCase()
      const targetTh = (translateDepartment(targetDept, 'th') || translateSection(targetDept, 'th') || '').trim().toLowerCase()

      const matchName = val => {
        if (!val || typeof val !== 'string') return false
        const norm = val.trim().toLowerCase()
        return norm === normTarget || (targetEn && norm === targetEn) || (targetTh && norm === targetTh)
      }

      // If supervisor mode: also match supervisor's own scope
      if (!this.isAdmin) {
        // 1. Match supervisor's user ID if supervisor is assigned
        const currentUserId = String(this.$store?.state?.auth?.user?._id || this.$store?.state?.auth?.user?.id || this.$store?.state?.auth?.profile?._id || this.$store?.state?.auth?.profile?.id || '')
        if (currentUserId) {
          const supId = String(ticket?.supervisorPart?.supervisor?._id || ticket?.supervisorPart?.supervisor || '')
          if (supId && supId === currentUserId) return true
        }

        // 2. Match supervisor's section ID directly against ticket category / responsibleDepartment
        const mySecId = this.supervisorSectionId
        if (mySecId) {
          const catId = String(ticket?.userPart?.category?._id || ticket?.userPart?.category || '')
          if (catId && catId === mySecId) return true
          const respId = String(ticket?.userPart?.responsibleDepartment?._id || ticket?.userPart?.responsibleDepartment || '')
          if (respId && respId === mySecId) return true
        }

        // 3. Match if ticket is assigned to any technician belonging to supervisor's section
        if (mySecId) {
          const assignments = this.getTicketAssignments(ticket)
          const isSupervisorTech = assignments.some(assignment => {
            const techId = this.getAssigneeId(assignment)
            if (!techId) return false
            const tech = (this.allTechnicians || []).find(t => String(t._id || t.id) === techId)
            if (!tech) return false
            const tSecId = String(tech.section?._id || tech.section?.id || tech.section || '')
            return tSecId === mySecId
          })
          if (isSupervisorTech) return true
        }
      }

      // 4. Check category object or name
      const cat = ticket?.userPart?.category
      if (cat && typeof cat === 'object') {
        const dName = cat.departmentName || cat.name || ''
        if (matchName(dName) || matchName(cat.code) || matchName(cat.nameEn) || matchName(cat.nameTh)) return true
      } else if (typeof cat === 'string' && cat.trim()) {
        const catStr = cat.trim()
        if (matchName(catStr)) return true
        const matchedCat = (this.categories || []).find(c =>
          String(c._id || c.id) === catStr ||
          matchName(c.departmentName) ||
          matchName(c.name) ||
          matchName(c.code) ||
          matchName(c.nameEn) ||
          matchName(c.nameTh)
        )
        if (matchedCat) {
          const mDept = matchedCat.departmentName || matchedCat.name || ''
          if (matchName(mDept) || matchName(matchedCat.nameEn) || matchName(matchedCat.nameTh)) return true
        }
      }

      // 5. Check responsible department
      const resp = ticket?.userPart?.responsibleDepartment
      if (resp && typeof resp === 'object') {
        const rName = resp.departmentName || resp.name || ''
        if (matchName(rName) || matchName(resp.nameEn) || matchName(resp.nameTh)) return true
      } else if (typeof resp === 'string' && matchName(resp)) {
        return true
      }

      // 6. Check subCategory lookup in categories
      const subName = ticket?.userPart?.subCategory?.name || ticket?.userPart?.subCategory
      if (subName && typeof subName === 'string') {
        const normSub = subName.trim().toLowerCase()
        const matchedCat = (this.categories || []).find(c => {
          const cDept = c.departmentName || c.name || ''
          if (!matchName(cDept) && !matchName(c.nameEn) && !matchName(c.nameTh)) return false
          return Array.isArray(c.subCategories) && c.subCategories.some(s => {
            const sName = (s?.name || s?.nameEn || s?.nameTh || s || '').trim().toLowerCase()
            return sName === normSub
          })
        })
        if (matchedCat) return true
      }

      // 7. Check assigned technicians
      const assignments = this.getTicketAssignments(ticket)
      const matchedTech = assignments.some(assignment => {
        const techId = this.getAssigneeId(assignment)
        if (!techId) return false
        const tech = (this.allTechnicians || []).find(t => String(t._id || t.id) === techId)
        if (!tech) return false
        const dName = tech.department?.name || tech.department?.departmentName || tech.section?.departmentName || (typeof tech.department === 'string' ? tech.department : '')
        const sName = tech.section?.departmentName || tech.section?.name || ''
        return matchName(dName) || matchName(sName)
      })
      if (matchedTech) return true

      return false
    },
    getFilteredTicketsForExport(deptOverride) {
      const targetDept = deptOverride !== undefined ? deptOverride : this.effectiveExcelDepartment
      return (this.tickets || []).filter(t => {
        if (t.status === 'cancelled') return false
        const date = moment(t.userPart?.dateTimeInformed || t.createdAt)
        if (this.pdfStartDate && this.pdfEndDate) {
          const start = moment(this.pdfStartDate).startOf('day')
          const end = moment(this.pdfEndDate).endOf('day')
          if (!date.isValid() || !date.isBetween(start, end, null, '[]')) {
            return false
          }
        } else if (!date.isValid()) {
          return false
        }
        return this.isTicketInDepartment(t, targetDept)
      })
    },
    setDatePreset(preset) {
      if (preset === 'all') {
        this.pdfStartDate = ''
        this.pdfEndDate = ''
      } else if (preset === 'thisMonth') {
        this.pdfStartDate = moment().startOf('month').format('YYYY-MM-DD')
        this.pdfEndDate = moment().endOf('month').format('YYYY-MM-DD')
      } else if (preset === 'last30Days') {
        this.pdfStartDate = moment().subtract(30, 'days').format('YYYY-MM-DD')
        this.pdfEndDate = moment().format('YYYY-MM-DD')
      } else if (preset === 'thisYear') {
        this.pdfStartDate = moment().startOf('year').format('YYYY-MM-DD')
        this.pdfEndDate = moment().endOf('year').format('YYYY-MM-DD')
      }
    },
    async downloadExcelReport() {
      try {
        this.isExportingReport = true
        await this.$nextTick()

        const flattenCategories = []
        this.categories.forEach(cat => {
          if (cat.subCategories && Array.isArray(cat.subCategories)) {
            cat.subCategories.forEach(sub => {
              flattenCategories.push({
                dep: cat.departmentName || cat.name,
                name: sub.name,
                sla: sub.slaHours
              })
            })
          }
        })

        const targetDepartment = this.effectiveExcelDepartment

        // 1. Filter tickets by date AND department
        const validTickets = this.getFilteredTicketsForExport(targetDepartment)

        if (!validTickets || validTickets.length === 0) {
          Swal.fire({ icon: 'info', title: this.$t('dashboard.noData') || 'ไม่มีข้อมูลตามเงื่อนไขที่เลือก' })
          this.isExportingReport = false
          return
        }

        // 2. Filter technicians for Sheet 2
        let displayTechs = []
        if (!this.isAdmin) {
          const mySecId = this.supervisorSectionId
          displayTechs = mySecId
            ? (this.allTechnicians || []).filter(tech => {
                const tSec = tech.section
                const tSecId = tSec && typeof tSec === 'object' ? String(tSec._id || tSec.id || '') : String(tSec || '')
                return !tSecId || tSecId === mySecId
              })
            : (this.allTechnicians || [])
        } else {
          displayTechs = (this.allTechnicians || [])
          if (targetDepartment !== 'all') {
            displayTechs = displayTechs.filter(tech => {
              const dName = tech.department?.name || tech.department?.departmentName || tech.section?.departmentName || tech.department
              const sName = tech.section?.departmentName || tech.section?.name || ''
              return (typeof dName === 'string' && dName.trim().toLowerCase() === targetDepartment.trim().toLowerCase()) ||
                     (typeof sName === 'string' && sName.trim().toLowerCase() === targetDepartment.trim().toLowerCase())
            })
          }
        }

        const evalItems = displayTechs.map(tech => {
          const techIdStr = String(tech._id || tech.id || '')
          const techTickets = validTickets.filter(t => this.isTicketAssignedTo(t, techIdStr))

          let totalJobs = techTickets.length
          let completedJobs = 0
          let onTimeJobs = 0
          let ratingSum = 0
          let ratedJobs = 0

          techTickets.forEach(t => {
            let slaHours = 24
            const subName = t.userPart?.subCategory?.name || t.userPart?.subCategory
            const matched = flattenCategories.find(s => s.name === subName)
            if (matched) slaHours = matched.sla

            const startTime = moment(t.userPart?.dateTimeInformed || t.createdAt)
            const deadline = t.slaDeadline ? moment(t.slaDeadline) : startTime.clone().add(slaHours, 'hours')
            const isClosed = ['done', 'welldone'].includes(t.status)
            let endTime = isClosed ? moment(t.technicianPart?.dateTimeFinished || t.updatedAt) : moment()
            if (isClosed) completedJobs++
            if (isClosed && !endTime.isAfter(deadline)) onTimeJobs++
            const stars = t.rating?.stars || 0
            if (stars > 0) { ratingSum += stars; ratedJobs++ }
          })

          const slaMetPercent = completedJobs > 0 ? Math.round((onTimeJobs / completedJobs) * 100) : 0
          const avgRating = ratedJobs > 0 ? (ratingSum / ratedJobs) : 0

          return {
            _id: tech._id,
            name: `${tech.name || ''} ${tech.surname || ''}`.trim() || tech.email || this.$t('common.technician') || 'ช่างเทคนิค',
            department: tech.department?.name || tech.department?.departmentName || tech.section?.departmentName || (typeof tech.department === 'string' ? tech.department : this.$t('common.other')),
            totalJobs,
            completedJobs,
            onTimeJobs,
            slaMetPercent,
            avgRating
          }
        })

        // Generate and download executive-grade styled Excel report
        exportSmartFixExcelReport({
          validTickets,
          evalItems,
          startDate: this.pdfStartDate,
          endDate: this.pdfEndDate,
          departmentName: targetDepartment,
          t: this.$t.bind(this),
          localeLang: this.$store.state.locale?.lang || 'th',
          formatDate: this.formatDate.bind(this)
        })

        this.showPdfModal = false
      } catch (err) {
        console.error('Failed to export Excel report:', err)
        Swal.fire({ icon: 'error', title: this.$t('mgmt.error') || 'Error', text: this.$t('evaluation.exportError') || this.$t('evaluation.excelExportError') || 'เกิดข้อผิดพลาดในการสร้างไฟล์รายงาน Excel' })
      } finally {
        this.isExportingReport = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../../assets/scss/staff-management";

.evaluation-container {
  width: 100%;
  padding: 0;
}

/* Report Modal Enhancements */
.report-modal-icon-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.2);
  color: #ffffff;
}

.report-scope-preview-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.preset-btn-group .btn {
  font-size: 0.82rem;
  font-weight: 600;
  border-color: #cbd5e1;
  color: #334155;
  background: #ffffff;
  padding: 6px 12px;

  &:hover {
    background: #e2e8f0;
    color: #1e40af;
  }

  &.active {
    background: #1e40af !important;
    border-color: #1e40af !important;
    color: #ffffff !important;
  }
}
::v-deep .evaluation-details-modal .modal-footer {
  display: none !important;
}

/* Green Theme Report Excel Button */
.btn-report-excel {
  background-color: #28a745 !important;
  border-color: #28a745 !important;
  color: #ffffff !important;
  font-weight: 700 !important;
  border-radius: 8px !important;
  white-space: nowrap;
  height: 31px;
  display: inline-flex;
  align-items: center;
  box-shadow: 0 2px 6px rgba(40, 167, 69, 0.3) !important;
  transition: all 0.2s ease-in-out !important;

  &:hover {
    background-color: #218838 !important;
    border-color: #1e7e34 !important;
    color: #ffffff !important;
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(40, 167, 69, 0.4) !important;
  }
}

/* Red Theme Details Button ( matching ติดตาม button ) */
.btn-eval-details {
  background: linear-gradient(135deg, #1e40af 0%, #0284c7 100%) !important;
  color: #ffffff !important;
  border: none !important;
  border-radius: 8px !important;
  font-weight: 700 !important;
  padding: 6px 14px !important;
  box-shadow: 0 2px 6px rgba(37, 99, 235, 0.25) !important;
  transition: all 0.2s ease-in-out !important;

  &:hover {
    background: linear-gradient(135deg, #1e3a8a 0%, #0369a1 100%) !important;
    color: #ffffff !important;
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(37, 99, 235, 0.35) !important;
  }
}

/* Modal Header styling for Desktop & Mobile */
::v-deep .modal-header {
  background: linear-gradient(135deg, #1e40af 0%, #0284c7 100%) !important;
  color: #ffffff !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
  padding: 12px 20px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;

  .modal-title {
    color: #ffffff !important;
    font-size: 1.1rem !important;
    font-weight: 700 !important;
  }
}

.evaluation-filter { margin-right:12px; padding:4px; border:1px solid rgba(37, 99, 235,.14); border-radius:10px; background:#fff; }
.evaluation-filter .form-control { color:#1e40af; font-weight:700; }
.evaluation-table ::v-deep .badge-info { color:#1e40af; background:#e0f2fe; }
.progress-xs {
  height: 4px;
}
@media (max-width:768px) {
  /* Push modal below top navbar on mobile */
  ::v-deep .modal-dialog,
  ::v-deep .evaluation-details-modal .modal-dialog {
    margin-top: 85px !important;
    margin-bottom: 25px !important;
  }

  .staff-ui-header { height:auto !important; min-height:112px; align-items:flex-start !important; flex-direction:column !important; }
  .filter-group { width:100%; flex-wrap:nowrap !important; }
  .evaluation-filter { flex:1 1 0; min-width:0; margin-right:8px; }
  .evaluation-filter:last-child { margin-right:0; }
  .evaluation-filter select { width:100%; min-width:0 !important; }

  /* Mobile Card Layout for Tables */
  ::v-deep .table-responsive-wrapper table.table {
    border: none !important;

    thead {
      position: absolute !important;
      top: -9999px !important;
      left: -9999px !important;
      opacity: 0 !important;
      height: 0 !important;
      overflow: hidden !important;
      pointer-events: none !important;
    }

    tbody {
      tr {
        display: block;
        background: #ffffff;
        border: 1px solid #e2e8f0 !important;
        border-radius: 12px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
        margin-bottom: 1rem;
        padding: 12px 14px;

        td {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0 !important;
          border: none !important;
          border-bottom: 1px dashed #f1f5f9 !important;

          &:last-child {
            border-bottom: none !important;
            padding-top: 12px !important;
            justify-content: center;
          }
        }
      }
    }
  }
}
</style>
